import Phaser from 'phaser';
import { HAPTICS, PALETTE, SPRINT } from '../../config';
import { createAthleteView, type AthleteView } from '../../game/AthleteView';
import { AudioEngine } from '../../game/AudioEngine';
import { getDiscipline } from '../../game/data/disciplines';
import { getSkin } from '../../game/data/skins';
import { countryFlag } from '../../game/data/flags';
import { pickOpponents } from '../../game/data/aiRoster';
import { t } from '../../game/i18n';
import type { DisciplineResult, Standing } from '../../game/types';
import { Button } from '../../ui/Button';
import { PowerMeter } from '../../ui/PowerMeter';
import { viewport } from '../../ui/viewport';
import { DisciplineScene } from './DisciplineScene';

type Side = 'L' | 'R';
type Phase = 'intro' | 'showcase' | 'spin' | 'flight' | 'between' | 'done';

const PIVOT_X = 150;
const PPM = SPRINT.pixelsPerMeter;
const MAX_ROT = 13; // rad/s ceiling
const IDEAL_LAUNCH = -Math.PI / 4; // up-and-forward (screen coords, y down)
const ATTEMPTS = 3;

/**
 * Hammer Throw — the rotation event. Alternate taps L/R to SPIN faster (the hammer orbits
 * the athlete); a release window glows when the hammer points up-and-forward. Tap LANCIA
 * to release: distance = spin speed × how good the release angle was. Long arcs (~<90 m).
 */
export class HammerThrow extends DisciplineScene {
  private phase: Phase = 'intro';
  private rig!: AthleteView;

  private rotSpeed = 0;
  private hammerAngle = 0;
  private lastSide: Side | null = null;
  private reach = 60;
  private throwDist = 0;
  private flightT = 0;
  private flightDur = 1;
  private releaseX = 0; private releaseY = 0;
  private releaseAngleRad = 0;
  private pivotY = 0;
  private radius = 74;
  private spinTaps = 0;
  private attempt = 1;
  private hammerBest = 0;
  private aiIndex = 0;
  private aiRig?: AthleteView;
  private playerMarker?: Phaser.GameObjects.GameObject[];

  private W = 0; private H = 0; private groundY = 0; private uiS = 1; private camTarget = 0;
  private aiThrows: { name: string; species: string; nationality: string; metric: number; skin: string }[] = [];

  private spinBar!: PowerMeter;
  private hud!: Phaser.GameObjects.Text;
  private bigText!: Phaser.GameObjects.Text;
  private throwBtn!: Button;
  private orbit!: Phaser.GameObjects.Graphics;

  constructor() { super('HammerThrow'); }

  create(): void {
    super.create();
    this.phase = 'intro';
    this.rotSpeed = 0; this.hammerAngle = 0; this.lastSide = null; this.throwDist = 0; this.flightT = 0; this.camTarget = 0; this.spinTaps = 0;
    this.attempt = 1; this.hammerBest = 0; this.aiIndex = 0; this.playerMarker = undefined; this.inWindowPrev = false;

    const { w, h, s } = viewport(this);
    this.W = w; this.H = h; this.uiS = s;
    this.groundY = h - Math.max(96, h * 0.2);
    this.reach = 38 + (this.athlete.stats.power / 100) * 42;
    const dispH = Phaser.Math.Clamp(h * 0.34, 170, 320);
    this.radius = Math.max(60, dispH * 0.42);
    this.pivotY = this.groundY - dispH * 0.5;

    const src = this.textures.get('stadium').getSourceImage();
    this.add.image(0, 0, 'stadium').setOrigin(0, 0).setScrollFactor(0).setDepth(-10)
      .setScale(Math.max(w / src.width, (this.groundY + 30) / src.height));
    this.drawField();
    this.orbit = this.add.graphics().setDepth(5);
    this.rig = createAthleteView(this, PIVOT_X, this.groundY, this.athlete, dispH);
    this.rig.container.setDepth(6);
    this.fetchWorldRecord('hammer_throw', 'distance', (m) => this.drawGroundRecord(PIVOT_X + m * PPM, this.groundY, this.groundY - Math.max(70, this.H * 0.18), `${m.toFixed(2)}m`));

    const opponents = pickOpponents(getSkin(this.athlete.skin).id, 3);
    this.aiThrows = opponents.map((o) => ({ name: o.name, species: o.species, nationality: o.nationality, skin: o.skin, metric: Math.max(25, 40 + (o.jumpDistance - 5) * 5 + (Math.random() * 12 - 6)) }));

    this.buildHud();
    this.controls.on('tapLeft', () => this.onTap('L'));
    this.controls.on('tapRight', () => this.onTap('R'));
    this.controls.on('action', () => this.release());
    this.startCountdown();
  }

  private drawField(): void {
    const endX = PIVOT_X + 95 * PPM;
    const g = this.add.graphics().setDepth(-6);
    g.fillStyle(0x4e8b4e, 1).fillRect(0, this.groundY, endX, this.H - this.groundY);
    g.fillStyle(0xcfcfd6, 1).fillEllipse(PIVOT_X, this.groundY + 6, 78, 24); // throwing circle
    g.lineStyle(3, 0xf4f7ff, 0.9).strokeEllipse(PIVOT_X, this.groundY + 6, 78, 24);
    for (let m = 20; m <= 90; m += 20) {
      const x = PIVOT_X + m * PPM;
      g.fillStyle(0x66a366, 1).fillRect(x, this.groundY, 2, 16);
      this.add.text(x, this.groundY + 20, `${m}m`, { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: '14px', color: '#cfe6cf' }).setOrigin(0.5, 0).setDepth(-6);
    }
    this.cameras.main.setBounds(0, 0, endX + this.W, this.H);
  }

  private buildHud(): void {
    const fs = (n: number) => `${Math.max(13, Math.round(n * this.uiS))}px`;
    this.hud = this.add.text(this.W / 2, 16, '', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(24), color: '#f4f7ff', fontStyle: 'bold' })
      .setOrigin(0.5, 0).setScrollFactor(0).setShadow(0, 3, '#000000aa', 5);
    const barW = Math.min(300, this.W * 0.5);
    const topY = Math.max(50, this.H * 0.13);
    this.spinBar = new PowerMeter(this, this.W / 2, topY, { width: barW, height: 18, fillColor: PALETTE.cadence });
    this.add.text(this.W / 2, topY - 18, t('hud.spinSpeed'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(12), color: '#d3dbf2' }).setOrigin(0.5).setScrollFactor(0);
    this.bigText = this.add.text(this.W / 2, this.H * 0.4, '', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(80), color: '#ffd24a', fontStyle: 'bold' })
      .setOrigin(0.5).setScrollFactor(0).setShadow(0, 8, '#00000088', 12);
    const label = this.sys.game.device.os.desktop ? `🎯 ${t('btn.throw')} ${t('btn.space')}` : `🎯 ${t('btn.throw')}`;
    this.throwBtn = new Button(this, this.W / 2, this.H - Math.max(30, 38 * this.uiS), label, () => this.release(), {
      width: Math.min(200, this.W * 0.4), height: Math.max(52, 58 * this.uiS), fontSize: Math.max(18, Math.round(22 * this.uiS)), color: PALETTE.power,
    });
    this.throwBtn.setScrollFactor(0).setAlpha(0.92).setVisible(false);
  }

  private startCountdown(): void {
    ['3', '2', '1', t('game.go')].forEach((label, i) => {
      this.time.delayedCall(i * 600, () => {
        this.bigText.setText(label).setAlpha(1).setScale(1.5);
        this.tweens.add({ targets: this.bigText, scale: 1, duration: 300, ease: 'Back.out' });
        if (label === t('game.go')) {
          AudioEngine.go();
          this.tweens.add({ targets: this.bigText, alpha: 0, delay: 250, duration: 200 });
          this.runAiShowcase();
        } else AudioEngine.countdownTick();
      });
    });
  }

  /** Each rival's animal spins the hammer and releases it to their best mark, then a flag. */
  private runAiShowcase(): void {
    this.phase = 'showcase';
    if (this.aiIndex >= this.aiThrows.length) { this.rig.container.setVisible(true); this.beginAttempt(); return; }
    this.rig.container.setVisible(false);
    const a = this.aiThrows[this.aiIndex]!;
    this.hud.setText(`🔨 ${a.name}`);
    this.aiRig?.destroy();
    this.aiRig = createAthleteView(this, PIVOT_X, this.groundY, { skin: a.skin }, Phaser.Math.Clamp(this.H * 0.34, 170, 320));
    this.aiRig.container.setDepth(6);
    this.camTarget = Phaser.Math.Clamp(PIVOT_X - this.W * 0.3, 0, this.cameras.main.getBounds().width - this.W);
    this.cameras.main.scrollX = this.camTarget;
    // Spin a few turns (accelerating), then release.
    this.hammerAngle = 0;
    const obj = { p: 0 };
    this.tweens.add({
      targets: obj, p: 1, duration: 1100, ease: 'Sine.in',
      onUpdate: () => { this.hammerAngle = obj.p * obj.p * Math.PI * 10; this.drawHammer(false); },
      onComplete: () => { this.aiRig?.setPose('celebrate'); this.aiThrowArc(a); },
    });
  }

  private aiThrowArc(a: { name: string; nationality: string; metric: number }): void {
    const landX = PIVOT_X + a.metric * PPM;
    const ball = this.ballPos();
    const releaseY = this.groundY - this.H * 0.32;
    const obj = { p: 0 };
    AudioEngine.whoosh();
    this.tweens.add({
      targets: obj, p: 1, duration: 700, ease: 'Sine.in',
      onUpdate: () => {
        const x = Phaser.Math.Linear(ball.x, landX, obj.p);
        const y = (this.groundY - 10) - Math.sin(obj.p * Math.PI) * this.H * 0.42 - (1 - obj.p) * (this.groundY - 10 - releaseY);
        this.orbit.clear();
        this.orbit.fillStyle(0x3a3f50, 1).fillCircle(x, y, 12);
        this.orbit.fillStyle(0xaab2c8, 0.9).fillCircle(x - 3, y - 3, 4);
        this.camTarget = Phaser.Math.Clamp(x - this.W * 0.45, 0, this.cameras.main.getBounds().width - this.W);
      },
      onComplete: () => {
        this.orbit.clear();
        this.flagMarker(landX, PALETTE.cadence, `${countryFlag(a.nationality)} ${a.name.split(' ')[0]} · ${a.metric.toFixed(2)}m`, false);
        AudioEngine.thud();
        this.tweens.add({ targets: this.aiRig!.container, alpha: 0, duration: 250, onComplete: () => { this.aiRig?.destroy(); this.aiRig = undefined; this.aiIndex++; this.runAiShowcase(); } });
      },
    });
  }

  private beginAttempt(): void {
    this.rotSpeed = 0; this.hammerAngle = 0; this.lastSide = null; this.spinTaps = 0; this.flightT = 0; this.inWindowPrev = false;
    this.phase = 'spin';
    this.orbit.clear();
    this.bigText.setAlpha(0);
    this.rig.container.setVisible(true).setAlpha(1);
    this.rig.setPose('idle');
    this.controls.setEnabled(true);
    this.throwBtn.setVisible(true).setScale(1).setAlpha(0.92);
    this.spinBar.setVisible(true);
    this.camTarget = Phaser.Math.Clamp(PIVOT_X - this.W * 0.3, 0, this.cameras.main.getBounds().width - this.W);
    this.cameras.main.scrollX = this.camTarget;
    this.hud.setText(t('game.attemptHammer', { n: this.attempt, max: ATTEMPTS }));
  }

  private flagMarker(x: number, color: number, label: string, mine: boolean): Phaser.GameObjects.GameObject[] {
    const poleH = Math.max(46, this.H * 0.11);
    const top = this.groundY - poleH;
    const g = this.add.graphics().setDepth(mine ? 6 : 4);
    g.fillStyle(0xffffff, 0.85).fillRect(x - 1.5, top, 3, poleH);
    g.fillStyle(color, 1).fillTriangle(x + 1.5, top, x + 1.5, top + 18, x + 26, top + 9);
    g.fillStyle(color, 1).fillCircle(x, this.groundY, 4);
    const t = this.add.text(x, top - 6, label, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(12 * this.uiS)}px`, color: '#0b1020', fontStyle: 'bold',
      backgroundColor: mine ? '#ffd24a' : '#bfe6ff', padding: { x: 5, y: 2 },
    }).setOrigin(0.5, 1).setDepth(mine ? 7 : 5);
    return [g, t];
  }

  private onTap(side: Side): void {
    if (this.phase !== 'spin') return;
    const alternated = side !== this.lastSide;
    this.lastSide = side;
    const gain = alternated ? 1.5 : 0.4;
    this.rotSpeed = Math.min(MAX_ROT, this.rotSpeed + gain);
    this.spinTaps++;
    AudioEngine.tap(alternated ? 0.6 : 0.2);
    this.vibrate(HAPTICS.tap);
  }

  /** True when the hammer's launch tangent points up-and-forward (good release window). */
  private launchDir(): number {
    // Rotating clockwise on screen; tangent leads the radius by +90°.
    return this.hammerAngle + Math.PI / 2;
  }

  private releaseQuality(): number {
    let d = Math.abs(this.angleDelta(this.launchDir(), IDEAL_LAUNCH));
    return Phaser.Math.Clamp(1 - d / (Math.PI / 2), 0, 1);
  }

  private angleDelta(a: number, b: number): number {
    let d = (a - b) % (Math.PI * 2);
    if (d > Math.PI) d -= Math.PI * 2;
    if (d < -Math.PI) d += Math.PI * 2;
    return d;
  }

  private release(): void {
    if (this.phase !== 'spin') return;
    const rotNorm = Phaser.Math.Clamp(this.rotSpeed / MAX_ROT, 0, 1);
    const angleQ = this.releaseQuality();
    if (rotNorm < 0.2) { // too slow to be valid — nudge the player
      this.hud.setText(t('game.tooSlowSpinThrow'));
      return;
    }
    this.throwDist = Math.max(5, rotNorm * this.reach * (0.4 + 0.6 * angleQ));
    const ld = this.launchDir();
    this.releaseAngleRad = Math.max(0.25, -Math.sin(ld) * 0.9 + 0.2); // upward component → arc height
    const ball = this.ballPos();
    this.releaseX = ball.x; this.releaseY = ball.y;
    this.phase = 'flight';
    this.flightT = 0;
    this.flightDur = Phaser.Math.Clamp(this.throwDist / 35, 0.8, 2.2);
    this.controls.setEnabled(false);
    this.throwBtn.setVisible(false);
    this.spinBar.setVisible(false);
    this.rig.setPose('celebrate');
    AudioEngine.whoosh();
    this.vibrate(HAPTICS.goodTap);
  }

  private ballPos(): { x: number; y: number } {
    return { x: PIVOT_X + Math.cos(this.hammerAngle) * this.radius, y: this.pivotY + Math.sin(this.hammerAngle) * this.radius * 0.7 };
  }

  update(_t: number, delta: number): void {
    const dt = delta / 1000;
    this.cameras.main.scrollX = Phaser.Math.Linear(this.cameras.main.scrollX, this.camTarget, 0.12);
    this.rig.update(dt);
    if (this.phase === 'spin') { this.updateSpin(dt); return; }
    if (this.phase === 'flight') { this.updateFlight(dt); }
  }

  private updateSpin(dt: number): void {
    this.rotSpeed = Math.max(0, this.rotSpeed - dt * 1.6); // bleeds without taps
    this.hammerAngle += this.rotSpeed * dt;
    const rotNorm = Phaser.Math.Clamp(this.rotSpeed / MAX_ROT, 0, 1);
    this.spinBar.set(rotNorm);
    const inWindow = this.releaseQuality() > 0.72 && rotNorm > 0.45;
    this.drawHammer(inWindow);
    // Telegraph the release moment on the button itself (where the thumb is): it grows and
    // goes fully opaque, with a haptic tick on entering the window.
    this.throwBtn.setScale(inWindow ? 1.12 : 1).setAlpha(inWindow ? 1 : 0.92);
    if (inWindow && !this.inWindowPrev) { this.hud.setText(t('game.throwNow')); this.vibrate(20); }
    else if (!inWindow && this.inWindowPrev) this.hud.setText(t('game.spinAccel'));
    this.inWindowPrev = inWindow;
    this.camTarget = Phaser.Math.Clamp(PIVOT_X - this.W * 0.3, 0, this.cameras.main.getBounds().width - this.W);
  }

  private inWindowPrev = false;

  private drawHammer(glow: boolean): void {
    const g = this.orbit;
    g.clear();
    const ball = this.ballPos();
    // Faint orbit guide.
    g.lineStyle(2, PALETTE.cadence, 0.12).strokeEllipse(PIVOT_X, this.pivotY, this.radius * 2, this.radius * 1.4);
    // Wire.
    g.lineStyle(3, 0xb9c0d6, 0.9).beginPath();
    g.moveTo(PIVOT_X, this.pivotY); g.lineTo(ball.x, ball.y); g.strokePath();
    // Ball (glows green in the release window).
    if (glow) { g.fillStyle(0x4ade80, 0.35).fillCircle(ball.x, ball.y, 22); }
    g.fillStyle(glow ? 0x6ef0a0 : 0x3a3f50, 1).fillCircle(ball.x, ball.y, 12);
    g.fillStyle(0xaab2c8, 0.9).fillCircle(ball.x - 3, ball.y - 3, 4);
  }

  private updateFlight(dt: number): void {
    this.flightT += dt;
    const p = Phaser.Math.Clamp(this.flightT / this.flightDur, 0, 1);
    const landX = PIVOT_X + this.throwDist * PPM;
    const x = Phaser.Math.Linear(this.releaseX, landX, p);
    const peak = this.H * 0.42 * Math.sin(Phaser.Math.Clamp(this.releaseAngleRad, 0.2, 1.3));
    const y = (this.groundY - 10) - Math.sin(p * Math.PI) * peak - (1 - p) * (this.groundY - this.releaseY);
    this.orbit.clear();
    this.orbit.lineStyle(5, 0xffffff, 0.08).beginPath();
    this.orbit.moveTo(x - 40, y); this.orbit.lineTo(x, y); this.orbit.strokePath();
    this.orbit.fillStyle(0x3a3f50, 1).fillCircle(x, y, 12);
    this.orbit.fillStyle(0xaab2c8, 0.9).fillCircle(x - 3, y - 3, 4);
    this.camTarget = Phaser.Math.Clamp(x - this.W * 0.45, 0, this.cameras.main.getBounds().width - this.W);
    if (p >= 1) this.land();
  }

  private land(): void {
    this.phase = 'between';
    const landX = PIVOT_X + this.throwDist * PPM;
    this.cameras.main.shake(160, 0.006);
    AudioEngine.thud();
    this.vibrate(HAPTICS.finish);
    const dust = this.add.particles(landX, this.groundY, 'spark', {
      lifespan: 560, speed: { min: 40, max: 150 }, angle: { min: 200, max: 340 },
      scale: { start: 0.6, end: 0 }, alpha: { start: 0.6, end: 0 }, tint: 0x9a9a4e, gravityY: 340, emitting: false,
    });
    dust.explode(16);
    this.time.delayedCall(800, () => dust.destroy());

    const isBest = this.throwDist > this.hammerBest;
    if (isBest) {
      this.hammerBest = this.throwDist;
      this.playerMarker?.forEach((o) => o.destroy());
      this.playerMarker = this.flagMarker(landX, PALETTE.sun, `${countryFlag(this.athlete.nationality)} ${this.athlete.name.split(' ')[0]} · ${this.hammerBest.toFixed(2)}m`, true);
      AudioEngine.fanfare();
    }
    this.bigText.setText(`${this.throwDist.toFixed(2)} m${isBest ? '  ⭐' : ''}`).setColor('#ffd24a').setAlpha(1).setScale(0.6);
    this.tweens.add({ targets: this.bigText, scale: 1, duration: 450, ease: 'Back.out' });
    this.hud.setText(t('game.attemptBest', { n: this.attempt, max: ATTEMPTS, m: this.hammerBest.toFixed(2) }));

    if (this.attempt < ATTEMPTS) {
      this.attempt++;
      this.time.delayedCall(1700, () => this.beginAttempt());
    } else {
      this.time.delayedCall(1900, () => this.finalize());
    }
  }

  private finalize(): void {
    this.phase = 'done';
    const standings: Standing[] = [
      { name: this.athlete.name, species: this.athlete.species, isPlayer: true, nationality: this.athlete.nationality, metric: this.hammerBest },
      ...this.aiThrows.map((a) => ({ name: a.name, species: a.species, isPlayer: false, nationality: a.nationality, metric: a.metric })),
    ].sort((x, y) => y.metric - x.metric);
    const rank = standings.findIndex((s) => s.isPlayer) + 1;
    const score = Math.round(Phaser.Math.Clamp(this.hammerBest * 12, 0, 1000));
    const reward = getDiscipline('hammer_throw').scoreToReward(score);
    const result: DisciplineResult = {
      disciplineId: 'hammer_throw', metricKind: 'distance', athleteId: this.athlete.id, athleteName: this.athlete.name,
      species: this.athlete.species, nationality: this.athlete.nationality, metric: this.hammerBest, score, reward, rank, standings,
    };
    void this.finishDiscipline(result);
  }

  private vibrate(pattern: number | number[]): void {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try { navigator.vibrate(pattern); } catch { /* unsupported */ }
    }
  }
}
